import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rptService } from '../../../services/rptService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  useTheme,
  TablePagination,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  People as PeopleIcon,
  List as ListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon
} from '@mui/icons-material';

const PropertyOwners = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [owners, setOwners] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('name_asc'); // name_asc, name_desc, address_asc, address_desc
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPropertiesModalOpen, setIsPropertiesModalOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerProperties, setOwnerProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [keepModalOpen, setKeepModalOpen] = useState(false);
  
  // Alert & Confirm states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  // Form state
  const [formData, setFormData] = useState({
    owner_name: '',
    address: ''
  });

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page on search or sort change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sortBy]);

  // Fetch paginated owners
  useEffect(() => {
    loadOwners();
  }, [page, rowsPerPage, debouncedSearch, sortBy]);

  const loadOwners = async () => {
    setLoading(true);
    try {
      const result = await rptService.getOwners({
        page,
        pageSize: rowsPerPage,
        search: debouncedSearch,
        sortBy
      });
      if (result.success) {
        setOwners(result.data || []);
        setTotalCount(result.count || 0);
      } else {
        showSnackbar('Error Loading Owners: ' + (result.error || 'Unable to load owners list.'), 'error');
      }
    } catch (error) {
      showSnackbar('Failed to Load Owners', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddOwner = async () => {
    if (!formData.owner_name || !formData.address) {
      showSnackbar('Please fill in Owner Name and Address', 'warning');
      return;
    }

    try {
      const result = await rptService.addOwner(formData);
      if (result.success) {
        showSnackbar(`Property owner "${formData.owner_name}" added successfully.`, 'success');
        resetForm();
        loadOwners();
        
        // Only close modal if keepModalOpen is false
        if (!keepModalOpen) {
          setIsAddModalOpen(false);
        }
      } else {
        showSnackbar('Error Adding Owner: ' + result.error, 'error');
      }
    } catch (error) {
      showSnackbar('System Error: Unable to add owner.', 'error');
      console.error(error);
    }
  };

  const handleEditOwner = async () => {
    if (!formData.owner_name || !formData.address) {
      showSnackbar('Please fill in Owner Name and Address', 'warning');
      return;
    }

    try {
      const result = await rptService.updateOwner(selectedOwner.owner_id, formData);
      if (result.success) {
        showSnackbar(`Owner "${formData.owner_name}" updated successfully.`, 'success');
        setIsEditModalOpen(false);
        resetForm();
        setSelectedOwner(null);
        loadOwners();
      } else {
        showSnackbar('Error Updating Owner: ' + result.error, 'error');
      }
    } catch (error) {
      showSnackbar('System Error: Unable to update owner.', 'error');
      console.error(error);
    }
  };

  const handleDeleteOwner = async (owner) => {
    showConfirm(
      'Delete Owner',
      `Are you sure you want to delete "${owner.owner_name}"? This action cannot be undone.`,
      async () => {
        setConfirmDialog({ ...confirmDialog, open: false });
        try {
          const result = await rptService.deleteOwner(owner.owner_id);
          if (result.success) {
            showSnackbar(`"${owner.owner_name}" deleted successfully.`, 'success');
            loadOwners();
          } else {
            showSnackbar('Error Deleting Owner: ' + result.error, 'error');
          }
        } catch (error) {
          showSnackbar('System Error: Unable to delete owner.', 'error');
          console.error(error);
        }
      }
    );
  };

  const openEditModal = (owner) => {
    setSelectedOwner(owner);
    setFormData({
      owner_name: owner.owner_name || '',
      address: owner.address || ''
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      owner_name: '',
      address: ''
    });
  };

  const handleAddProperty = (owner) => {
    navigate(`/portal/property-tax/properties?owner_id=${owner.owner_id}`);
  };

  const handleViewProperties = async (owner) => {
    setSelectedOwner(owner);
    setIsPropertiesModalOpen(true);
    setLoadingProperties(true);
    
    try {
      const result = await rptService.getPropertiesByOwner(owner.owner_id);
      if (result.success) {
        setOwnerProperties(result.data);
      } else {
        showSnackbar('Error Loading Properties: ' + result.error, 'error');
        setOwnerProperties([]);
      }
    } catch (error) {
      showSnackbar('Failed to Load Properties', 'error');
      console.error(error);
      setOwnerProperties([]);
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleExport = async () => {
    if (totalCount === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }
    setExporting(true);
    try {
      const result = await rptService.getOwnersForExport({
        search: debouncedSearch,
        sortBy
      });
      if (!result.success || !result.data || result.data.length === 0) {
        showSnackbar('No data to export', 'warning');
        return;
      }

      const csvContent = [
        ['Owner Name', 'Address', 'Registration Date'],
        ...result.data.map(owner => [
          `"${(owner.owner_name || '').replace(/"/g, '""')}"`,
          `"${(owner.address || '').replace(/"/g, '""')}"`,
          `"${owner.created_at ? new Date(owner.created_at).toLocaleDateString() : ''}"`
        ])
      ].map(e => e.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `property_owners_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      showSnackbar('Failed to Export Owners CSV', 'error');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/portal/property-tax/dashboard')}
            >
              RPT System
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>Property Owners</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <PeopleIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Property Owners
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', lineHeight: 1.6 }}>
              Manage property owners and their information
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading || exporting || totalCount === 0}
              sx={{ height: 48, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={() => { resetForm(); setKeepModalOpen(false); setIsAddModalOpen(true); }}
              sx={{ 
                bgcolor: '#1e293b', 
                '&:hover': { bgcolor: '#334155' },
                px: 3,
                py: 1.5,
                borderRadius: 1.5,
                fontWeight: 600,
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
              }}
            >
              Add New Owner
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="medium">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                  <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                  <MenuItem value="address_asc">Address (A-Z)</MenuItem>
                  <MenuItem value="address_desc">Address (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Owners List */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h5" fontWeight="600">
              Owner Directory
            </Typography>
            <Chip 
              label={`${totalCount} owner${totalCount !== 1 ? 's' : ''}`} 
              color="primary" 
              variant="outlined" 
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <Typography>Loading owners...</Typography>
            </Box>
          ) : owners.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 8, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No owners found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'Try adjusting your search' : 'Add your first property owner to get started'}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell>Owner Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {owners.map((owner) => (
                      <TableRow 
                        key={owner.owner_id} 
                        hover
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: '#ffffff' },
                          '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                          '&:hover': { bgcolor: '#f1f5f9 !important' },
                          transition: 'background-color 0.1s'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ 
                              bgcolor: 'primary.light', 
                              color: 'primary.main', 
                              p: 1, 
                              borderRadius: 1, 
                              mr: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <PersonIcon />
                            </Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {owner.owner_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{owner.address || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="View Properties">
                              <IconButton size="small" color="primary" onClick={() => handleViewProperties(owner)}>
                                <ListIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Add Property">
                              <IconButton size="small" color="success" onClick={() => handleAddProperty(owner)}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Owner">
                              <IconButton size="small" color="info" onClick={() => openEditModal(owner)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Owner">
                              <IconButton size="small" color="error" onClick={() => handleDeleteOwner(owner)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Box>

        {/* Footer */}
        <Box component="footer" sx={{ py: 6, textAlign: 'center', borderTop: '1px solid #e2e8f0', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Government Portal
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Authorized Personnel Only • Confidential Information
          </Typography>
        </Box>
      </Container>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon /> Add New Property Owner
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Owner Name"
              fullWidth
              required
              value={formData.owner_name}
              onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
              placeholder="Full name of property owner"
            />
            <TextField
              label="Address"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Complete address"
            />
            <FormControlLabel
              control={
                <Checkbox 
                  checked={keepModalOpen} 
                  onChange={(e) => setKeepModalOpen(e.target.checked)} 
                />
              }
              label="Keep dialog open for continuous entry"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setIsAddModalOpen(false); setKeepModalOpen(false); }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleAddOwner} startIcon={<SaveIcon />}>
            Add Owner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon /> Edit Property Owner
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Owner Name"
              fullWidth
              required
              value={formData.owner_name}
              onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
            />
            <TextField
              label="Address"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleEditOwner} startIcon={<SaveIcon />}>
            Update Owner
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Properties Modal */}
      <Dialog open={isPropertiesModalOpen} onClose={() => setIsPropertiesModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon /> Properties of {selectedOwner?.owner_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {loadingProperties ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <Typography>Loading properties...</Typography>
              </Box>
            ) : ownerProperties.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <HomeIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No properties found
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  This owner doesn't have any properties registered yet.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setIsPropertiesModalOpen(false);
                    handleAddProperty(selectedOwner);
                  }}
                >
                  Add Property
                </Button>
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                      <TableRow>
                        <TableCell>TD Number / PIN</TableCell>
                        <TableCell>Barangay</TableCell>
                        <TableCell>Kind / Class</TableCell>
                        <TableCell align="right">Assessed Value</TableCell>
                        <TableCell align="center">Effectivity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ownerProperties.map(property => (
                        <TableRow key={property.property_id} hover>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {property.td_number}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              PIN: {property.pin || 'N/A'}
                            </Typography>
                            {property.arpn && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                ARPN: {property.arpn}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {property.barangay || 'N/A'}
                            </Typography>
                            {property.lot_number && (
                              <Typography variant="caption" color="text.secondary">
                                Lot {property.lot_number}{property.block_number && `, Blk ${property.block_number}`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="column" spacing={0.5} alignItems="flex-start">
                              {property.kind_name && (
                                <Chip label={property.kind_name} size="small" color="info" variant="outlined" />
                              )}
                              {property.classification_name && (
                                <Chip label={property.classification_name} size="small" variant="outlined" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {property.assessed_value 
                                ? `₱${parseFloat(property.assessed_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : 'N/A'
                              }
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={property.effectivity_year} size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ownerProperties.length}</strong> propert{ownerProperties.length !== 1 ? 'ies' : 'y'} found
                  </Typography>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="primary" 
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setIsPropertiesModalOpen(false);
                      handleAddProperty(selectedOwner);
                    }}
                  >
                    Add Another Property
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsPropertiesModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button onClick={confirmDialog.onConfirm} color="error" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PropertyOwners;
